import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios";

/**
 * Axios client — с credentials (cookies) + base URL из Vite env.
 *
 * dev: proxy через vite.config.ts → http://localhost:8000
 * prod: same-origin (frontend и backend на одном host через Traefik) либо
 *       VITE_API_BASE_URL (если разные домены)
 */
export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ?? "",
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

const ADMIN_JWT_KEY = "cp_admin_jwt";

/** Decode JWT payload без верификации подписи (только для exp check). */
function decodeJwtExp(jwt: string): number | null {
  try {
    const parts = jwt.split(".");
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1]!.replace(/-/g, "+").replace(/_/g, "/")));
    return typeof payload.exp === "number" ? payload.exp : null;
  } catch {
    return null;
  }
}

/** Проверка JWT на истечение (без serverside verify — только exp claim). */
export function isJwtExpired(jwt: string | null): boolean {
  if (!jwt) return true;
  const exp = decodeJwtExp(jwt);
  if (!exp) return true;
  // 30 секунд buffer чтобы не использовать прямо у границы
  return exp * 1000 < Date.now() + 30_000;
}

export function clearAdminJwt(): void {
  localStorage.removeItem(ADMIN_JWT_KEY);
}

export function getAdminJwt(): string | null {
  const jwt = localStorage.getItem(ADMIN_JWT_KEY);
  if (!jwt) return null;
  // Pre-validate: если истёк — auto-clear + return null
  if (isJwtExpired(jwt)) {
    clearAdminJwt();
    return null;
  }
  return jwt;
}

/** Attach admin JWT (если валидный) к admin requests. */
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (config.url?.startsWith("/api/admin")) {
      const jwt = getAdminJwt();
      if (jwt) {
        config.headers.Authorization = `Bearer ${jwt}`;
      }
    }
    return config;
  },
);

/** Response interceptor: 401/403 на admin endpoint → auto-clear JWT + reload. */
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const url = error.config?.url ?? "";
    const status = error.response?.status;

    if (
      (status === 401 || status === 403) &&
      url.startsWith("/api/admin")
    ) {
      // Очистить просроченный admin JWT
      const had = !!localStorage.getItem(ADMIN_JWT_KEY);
      if (had) {
        clearAdminJwt();
        // Notify user once + reload to admin login form
        // Используем sessionStorage чтобы избежать мульти-alert при concurrent requests
        if (!sessionStorage.getItem("cp_jwt_expired_notified")) {
          sessionStorage.setItem("cp_jwt_expired_notified", "1");
          // Reload через requestAnimationFrame чтобы UI показал что-то перед reload
          window.requestAnimationFrame(() => {
            // Если уже на /admin — просто reload (покажется AdminLoginPrompt)
            // Если на другой admin-зависимой странице — push to /admin
            if (location.pathname.startsWith("/admin")) {
              location.reload();
            } else {
              location.href = "/admin";
            }
          });
        }
      }
    }

    return Promise.reject(error);
  },
);
